import * as React from 'react';
import { Action, Expander, FulcrumEditor, Spellcheck } from '@fulcrumgt/react-fulcrum-editor';
import { FulcrumEditorProps, FulcrumEditorTheme } from '@fulcrumgt/react-fulcrum-editor/types/FulcrumEditor';
import { inject, observer } from 'mobx-react';
import { RootStore } from 'store/root.store';
import NarrativeStore from 'store/narrative.store';
import NarrativeCodesDialog from 'components/NarrativeCodesDialog';
import NarrativeCodesDialogStore from 'store/narrativeCodes.dialog.store';
import { AiNarrativeResponse, CustomDictionary, Narrative } from '../../api/types/types';
import { theme } from 'common/theme';
import CreateNarrativeCodeDialogStore from 'store/createNarrativeCodeDialog.store';
import CustomDictionaryStore from 'store/customDictionary.store';
import { PluginName } from '@fulcrumgt/react-fulcrum-editor/types/Plugins/Plugin';
import { withTranslation } from 'react-i18next';
import * as Styled from './styled';
import AiComponent from './AiComponent';
import ImmutableTimeEntry from 'api/immutables/ImmutableTimeEntry';

interface NarrativeState {
    dictionaryKey?: string;
    aiMinLengthWarning?: string;
    isFocusAtEnd?: boolean;
}
interface Props extends FulcrumEditorProps {
    disableNarrativeContextMenuActions?: boolean,
    dictionaryKey?: string,
    changeLanguage?: (key: string) => void,
    // stores
    narrativeStore?: NarrativeStore,
    codesStore?: NarrativeCodesDialogStore,
    createNarrativeCodeDialogStore?: CreateNarrativeCodeDialogStore,
    customDictionaryStore?: CustomDictionaryStore,
    highlight?: () => void;
    // tslint:disable-next-line:no-any
    t?: any
    // AI related
    enableAI: boolean;
    timeEntry?: ImmutableTimeEntry;
    aiOnPost?: boolean;
    isGridView?: boolean;
    aiResponse?: AiNarrativeResponse | null | string;
    setAiResponse?: (res: AiNarrativeResponse | null | string) => void;
    updateAiUsageCount?: () => void;
    updateNoChangeRequiredCount?: () => void;
    aiUsageCount?: number;
    onChange: (text: string, acceptedAi?: boolean) => void;
}

const FULCRUM_EDITOR_THEME: FulcrumEditorTheme = {
    primaryColor: theme.primary.main,
    errorColor: theme.error
};

@inject((allStores: { rootStore: RootStore }) => {
    let rootStore = allStores.rootStore;
    return {
        createNarrativeCodeDialogStore: rootStore.createNarrativeCodeDialogStore,
        codesStore: rootStore.narrativeCodesDialogStore,
        narrativeStore: rootStore.narrativeStore,
        customDictionaryStore: rootStore.customDictionaryStore
    };
})
@observer
class NarrativeField extends React.Component<Props, NarrativeState> {
    static defaultProps = {
        // default fulcrum-editor props:
        contextMenuStyle: { zIndex: 1337 },
        theme: FULCRUM_EDITOR_THEME,
        debouncedUpdate: true,
        maxLength: 255,
        style: {
            maxHeight: '100px',
            overflow: 'auto'
        },
        showCharCount: true,
        disableNarrativeContextMenuActions: false,
        enableAI: false
    }

    constructor(props: Props) {
        super(props);
        this.state = {
            dictionaryKey: this.props.dictionaryKey,
            aiMinLengthWarning: '',
            isFocusAtEnd: false // used to move cursor to the end on paste text with extra spaces
        }
    }

    componentWillReceiveProps(nextProps: Readonly<Props>): void {
        this.setState({ dictionaryKey: nextProps.dictionaryKey });
    }

    changeDictionary = (key: string) => {
        if (this.props.changeLanguage) {
            this.props.changeLanguage(key);
        }
        this.setState({ dictionaryKey: key });
    };

    componentDidUpdate() {
        this.props.customDictionaryStore!.ignoreCustomWords();
        if (this.props.highlight) {
            this.props.highlight();
        }
    }

    // tslint:disable-next-line:no-any
    handleFocus = (ref: any) => {
        if (this.state.isFocusAtEnd) {
            ref.focusAtEnd();
            this.setState({ isFocusAtEnd: false });
        }
    }

    handleChange = (text: string) => {
        if (text.length  > (this.props.value || '').length + 10) {
            const cleanText = text.replace(/\s+/g, ' ').trim();
            this.props.onChange(cleanText);
            // if spaces are trimmed/removed then set focus to end
            if (text.length > cleanText.length) {
                this.setState({ isFocusAtEnd: true });
            }
        } else {
            this.props.onChange(text);
        }
    }

    handleAcceptSuggestion = (suggestion: string) => {
        this.props.onChange(suggestion, true);
    }

    setAiWarning = (message: string) => {
        this.setState({ aiMinLengthWarning : message });
    }

    render() {
        const {
            t,
            value,
            disabled,
            placeholder,
            narrativeStore,
            style,
            helperText,
            error,
            enableAI,
            timeEntry,
            isGridView,
            aiOnPost,
            aiResponse,
            aiUsageCount,
            setAiResponse,
            updateAiUsageCount,
            updateNoChangeRequiredCount
        } = this.props;
        const { features, disableAiNarrativeRewrite, setPreviousActiveElement } = narrativeStore!.rootStore.appStore;
        const isAiEnabled = features.EpochConfigAINarrativeRewrite && !disableAiNarrativeRewrite && enableAI && !disabled;
        const helperTxt = helperText ? helperText : this.state.aiMinLengthWarning;
        const err = error ? true : !!this.state.aiMinLengthWarning;
        let props = isAiEnabled ? {
            ...this.props,
            placeholder: undefined,
            helperText: helperTxt,
            error: err,
            style: {...style}
        } : this.props;

        props = {
            ...props,
            onChange: this.handleChange,
            inputRef: this.handleFocus
        };

        return (
            <>
                <Styled.Contianer color={FULCRUM_EDITOR_THEME.primaryColor}>
                    {isAiEnabled &&
                        <Styled.LabelContainer
                            // selector is defined in Fulcrum Editor
                            onClick={(e) =>
                                (e.currentTarget.parentElement!.querySelector('[data-editor-id] > * [role="textbox"]') as HTMLElement).focus()
                            }
                        >
                            <Styled.InputLabel shrink={!!value}>
                                {placeholder}
                            </Styled.InputLabel>
                        </Styled.LabelContainer>
                    }
                    <FulcrumEditor {...props}>
                        <Spellcheck
                            maxSuggestions={5}
                            dictionary={this.state.dictionaryKey}
                            onDictionaryChange={(key) => this.changeDictionary(key)}
                        />
                        <Action
                            disabled={this.props.disableNarrativeContextMenuActions}
                            act={async (editorActions) => {
                                setPreviousActiveElement();
                                let narratives = this.props.codesStore!.narratives;
                                const response = await this.props.codesStore!.open(
                                        narratives ? narratives : []
                                    )
                                    .catch(() => null);

                                if (response && response.replacement) {
                                    const narrative = response as Narrative;
                                    editorActions.replaceSelection(narrative.replacement);
                                    editorActions.focus();
                                }
                                editorActions.focus();
                            }}
                        >
                            {t('narrative_field.action.narratives')}
                        </Action>
                        <Action
                            disableWhen={context => !!context.selection && context.selection.length > 0}
                            disabled={this.props.disableNarrativeContextMenuActions}
                            act={async (editorActions) => {
                                setPreviousActiveElement();
                                const store = this.props.createNarrativeCodeDialogStore!;

                                store.setNarrativeKey('');
                                store.setNarrativeReplacement(editorActions.context.selection);

                                await store.open().catch(() => null);
                                editorActions.focus();
                            }}
                        >
                            {t('narrative_field.action.create')}
                        </Action>
                        <Action
                            act={async (editorActions) => {
                                const { selection, entityUnderCursor } = editorActions.context;
                                const store = this.props.customDictionaryStore!;
                                const dictObj = new CustomDictionary(
                                    !!selection.trim() ? selection.trim() : entityUnderCursor.data.word
                                );
                                store.addNewWord(dictObj).then(() => {
                                    editorActions.updatePlugins(true, 'SPELLCHECK' as PluginName);
                                });
                            }}
                            disableWhen={context => {
                                const store = this.props.customDictionaryStore!;
                                return ((!!context.selection &&
                                    context.selection.trim().split(' ').length === 1 &&
                                    !store.isWordDuplicate(context.selection.trim()))
                                    && context.selection.trim().length !== 0) ||
                                    (!!context.entityUnderCursor && !context.entityUnderCursor.data.isCorrect);
                            }}
                        >
                            {t('narrative_field.action.add_to_dictionary')}
                        </Action>
                        <Expander prefix={'\\'} provider={this.expand}/>
                    </FulcrumEditor>
                    {isAiEnabled &&
                        <AiComponent
                            timeEntry={timeEntry!}
                            narrativeText={value}
                            setWarning={this.setAiWarning}
                            onAcceptSuggestion={this.handleAcceptSuggestion}
                            updateAiUsageCount={updateAiUsageCount!}
                            updateNoChangeRequiredCount={updateNoChangeRequiredCount!}
                            isGridView={isGridView}
                            aiOnPost={aiOnPost!}
                            aiResponse={aiResponse}
                            setAiResponse={setAiResponse}
                            aiUsageCount={aiUsageCount}
                            error={err}
                        />
                    }
                </Styled.Contianer>
                <NarrativeCodesDialog />
            </>
        );
    }
    
    private expand = async (word: string): Promise<string | null> => {

        if (this.props.narrativeStore!.allNarratives.length <= 0) {
            await this.props.narrativeStore!.fetchAllNarratives();
        }

        const possibleReplacement = this.props.narrativeStore!.allNarratives.filter(narrative => {
            return !narrative.deleted && narrative.key.toLowerCase() === word.toLowerCase();
        });

        // global and local narrative stored in separate variables 
        let locNarrResults = possibleReplacement.filter((entry) => entry.global === false);
        let gloNarrResults = possibleReplacement.filter((entry) => entry.global === true);

        // fetching global and local narratives
        if (possibleReplacement.length > 0) {
            return locNarrResults.length === 1 ? locNarrResults[0].replacement : gloNarrResults[0].replacement;
        }

        return null;
    };
}

export default withTranslation(['narratives'])(NarrativeField);